import { lazy, Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import {
  canConfigureBot,
  homeRoute,
  isAdmin,
  isAnalyticsOnly,
  isAnalyticsPath,
  useIsAuthenticated,
  useSessionUser,
} from '@/entities/session';
import { routes } from '@/shared/config';
import { FullPageLoader } from '@/shared/ui';

const ForbiddenPage = lazy(() =>
  import('@/pages/status/forbidden').then((m) => ({ default: m.ForbiddenPage })),
);

/**
 * Signed-out users go to login (remembering the target). Analytics-only users are confined to
 * the analytics screens (and their profile), since the API refuses every other module for them.
 */
export function ProtectedRoute() {
  const isAuthenticated = useIsAuthenticated();
  const user = useSessionUser();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Navigate to={routes.login} replace state={{ from: location.pathname + location.search }} />
    );
  }
  if (isAnalyticsOnly(user) && !isAnalyticsPath(location.pathname)) {
    return <Navigate to={homeRoute(user)} replace />;
  }
  return <Outlet />;
}

/** `/` sends each user to their start page. */
export function HomeRedirect() {
  const user = useSessionUser();
  return <Navigate to={homeRoute(user)} replace />;
}

/**
 * Keeps signed-in users away from guest-only pages such as login. Right after sign-in this is
 * what sends the user on, so it honours the page they originally requested.
 */
export function GuestRoute() {
  const isAuthenticated = useIsAuthenticated();
  const user = useSessionUser();
  const location = useLocation();
  if (!isAuthenticated) return <Outlet />;
  const from = (location.state as { from?: string } | null)?.from;
  return <Navigate to={from && from !== routes.login ? from : homeRoute(user)} replace />;
}

/** ADMIN / SUPERADMIN only; others see the access-denied page in place (production behaviour). */
export function AdminRoute() {
  const user = useSessionUser();
  if (isAdmin(user)) return <Outlet />;
  return (
    <Suspense fallback={<FullPageLoader />}>
      <ForbiddenPage />
    </Suspense>
  );
}

/**
 * Admins, or users whose permissionType is BOT_CONFIGURATION.
 *
 * Production only hid the entry from the navigation, so a deep link to `/bot-configuration` let
 * anyone queue a bot run. The guard closes that without changing who the menu allows.
 */
export function BotRoute() {
  const user = useSessionUser();
  if (canConfigureBot(user)) return <Outlet />;
  return (
    <Suspense fallback={<FullPageLoader />}>
      <ForbiddenPage />
    </Suspense>
  );
}
